import React from 'react';

import { useTranslation } from '../../../../hooks/useTranslation/useTranslation';
import { getAssetUrl } from '../../utils/gameUtils';
import styles from './GamePillars.module.css';
import { GamePillarsProps } from './GamePillars.types';

export const GamePillars: React.FC<GamePillarsProps> = ({ pillars, vessels }) => {
  const { t } = useTranslation();

  const getPillarIcon = (index: number) => {
    switch (index) {
      case 0:
        // Helm wheel icon
        return (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="9" />
            <circle cx="12" cy="12" r="3" />
            <line x1="12" y1="3" x2="12" y2="9" />
            <line x1="12" y1="15" x2="12" y2="21" />
            <line x1="3" y1="12" x2="9" y2="12" />
            <line x1="15" y1="12" x2="21" y2="12" />
          </svg>
        );
      case 1:
        // Wave icon
        return (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M2 12c2.5-3 5-3 7.5 0s5 3 7.5 0 5-3 7 0" />
            <path d="M2 17c2.5-3 5-3 7.5 0s5 3 7.5 0 5-3 7 0" />
          </svg>
        );
      case 2:
        // Wind icon
        return (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2" />
          </svg>
        );
      default:
        // Compass icon
        return (
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
          </svg>
        );
    }
  };

  return (
    <div className={styles.container}>
      {/* Simulation Pillars */}
      <section className={styles.sectionBlock} aria-labelledby="pillars-heading">
        <div className={styles.sectionHeader}>
          <h2 id="pillars-heading" className={styles.sectionTitle}>
            {t.games.overview.pillarsTitle}
          </h2>
          <p className={styles.sectionSubtitle}>{t.games.overview.visionSubtitle}</p>
        </div>

        <div className={styles.pillarsGrid}>
          {pillars.map((pillar, idx) => (
            <article key={pillar.id} className={styles.pillarCard}>
              <div className={styles.pillarTop}>
                <div className={styles.pillarIconWrapper} aria-hidden="true">
                  {getPillarIcon(idx)}
                </div>
                <span className={styles.pillarBadge}>{pillar.badge}</span>
              </div>
              <h3 className={styles.pillarTitle}>{pillar.title}</h3>
              <p className={styles.pillarDesc}>{pillar.description}</p>
            </article>
          ))}
        </div>
      </section>

      {/* Vessels & Craft Showcase */}
      <section className={styles.sectionBlock} aria-labelledby="vessels-heading">
        <div className={styles.sectionHeader}>
          <h2 id="vessels-heading" className={styles.sectionTitle}>
            {t.games.vessels.title}
          </h2>
          <p className={styles.sectionSubtitle}>{t.games.vessels.subtitle}</p>
        </div>

        <div className={styles.vesselsGrid}>
          {vessels.map((vessel) => (
            <article key={vessel.id} className={styles.vesselCard}>
              <div className={styles.vesselHeader}>
                <h3 className={styles.vesselName}>{vessel.name}</h3>
                <span className={styles.vesselType}>{vessel.type}</span>
              </div>
              {vessel.imageUrl && (
                <div className={styles.vesselImageContainer}>
                  <img
                    src={getAssetUrl(vessel.imageUrl)}
                    alt={vessel.imageAlt || vessel.name}
                    className={styles.vesselImage}
                    loading="lazy"
                  />
                  {vessel.viewType && (
                    <span className={styles.vesselViewBadge}>{vessel.viewType}</span>
                  )}
                </div>
              )}
              <p className={styles.vesselDesc}>{vessel.description}</p>
              <div className={styles.vesselSpecsList}>
                <span className={styles.specChip}>{vessel.speed}</span>
                <span className={styles.specChip}>{vessel.crew}</span>
                <span className={styles.specChip}>{vessel.handling}</span>
              </div>
            </article>
          ))}
        </div>
        <p className={styles.vesselsDisclaimer}>
          <span className={styles.disclaimerAsterisk} aria-hidden="true">
            *
          </span>{' '}
          {t.games.vessels.assetsDisclaimer}
        </p>
      </section>
    </div>
  );
};
